import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import AuthGuard from "../components/AuthGuard";
import { api } from "../api/client";
import { Upload, X, Camera, AlertCircle } from "lucide-react";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const REASONS = [
    { value: "damaged", label: "Damaged on arrival", photoReq: true },
    { value: "expired", label: "Expired / near expiry", photoReq: true },
    { value: "wrong_product", label: "Wrong product delivered", photoReq: true },
    { value: "quality_issue", label: "Quality issue", photoReq: true },
    { value: "not_as_described", label: "Not as described", photoReq: false },
    { value: "allergic_reaction", label: "Allergic reaction", photoReq: false },
    { value: "other", label: "Other", photoReq: false },
];

const ReturnNew = () => {
    const [sp] = useSearchParams();
    const navigate = useNavigate();
    const orderId = sp.get("order_id");
    const [order, setOrder] = useState(null);
    const [items, setItems] = useState({}); // { idx: { selected, qty, reason, notes, photos: [{id,url}] } }
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const fileRef = useRef(null);
    const [activeIdx, setActiveIdx] = useState(null);

    useEffect(() => {
        if (!orderId) return;
        api.get(`/orders/${orderId}`).then((r) => setOrder(r.data)).catch(() => setError("Order not found."));
    }, [orderId]);

    const updateItem = (idx, patch) => setItems((prev) => ({ ...prev, [idx]: { ...(prev[idx] || { selected: true, qty: 1, reason: "damaged", notes: "", photos: [] }), ...patch } }));

    const triggerUpload = (idx) => {
        setActiveIdx(idx);
        fileRef.current?.click();
    };

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f || activeIdx == null) return;
        if (f.size > 4 * 1024 * 1024) { toast.error("Max file size 4 MB"); return; }
        const form = new FormData();
        form.append("file", f);
        try {
            const { data } = await api.post("/returns/upload-photo", form, { headers: { "Content-Type": "multipart/form-data" } });
            const idx = activeIdx;
            setItems((prev) => {
                const cur = prev[idx] || { selected: true, qty: 1, reason: "damaged", notes: "", photos: [] };
                return { ...prev, [idx]: { ...cur, photos: [...(cur.photos || []), data].slice(0, 4) } };
            });
            toast.success("Photo uploaded");
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Upload failed");
        }
    };

    const removePhoto = (idx, photoId) => setItems((prev) => ({ ...prev, [idx]: { ...prev[idx], photos: (prev[idx]?.photos || []).filter((p) => p.id !== photoId) } }));

    const selectedItems = useMemo(() => Object.entries(items).filter(([, v]) => v.selected), [items]);
    const totalRefund = useMemo(() => selectedItems.reduce((s, [idx, v]) => s + Number(v.qty || 0) * Number(order?.items?.[idx]?.selling_price || 0), 0), [selectedItems, order]);

    const submit = async () => {
        setError(null);
        if (!selectedItems.length) { setError("Select at least one item to return."); return; }
        const payload = {
            order_id: orderId,
            items: selectedItems.map(([idx, v]) => ({
                order_item_index: Number(idx),
                qty: Number(v.qty || 1),
                reason: v.reason,
                photo_urls: (v.photos || []).map((p) => p.url),
                notes: v.notes || "",
            })),
        };
        setSubmitting(true);
        try {
            const { data } = await api.post("/returns", payload);
            toast.success("Return requested — we're reviewing it now.");
            navigate(`/returns/${data.id}`);
        } catch (err) {
            const d = err?.response?.data?.detail;
            setError(typeof d === "string" ? d : d?.message || "Could not request return.");
        } finally {
            setSubmitting(false);
        }
    };

    if (!orderId) return (
        <AuthGuard>
            <Navbar />
            <main className="mx-auto max-w-2xl p-10 text-center">
                <p>Missing order reference. <Link className="text-brand-700 hover:underline" to="/orders">Go to orders</Link></p>
            </main>
        </AuthGuard>
    );

    return (
        <AuthGuard>
            <div className="min-h-screen bg-white dark:bg-[#0B0B12]">
                <Navbar />
                <main className="mx-auto max-w-3xl px-6 py-10 lg:px-12">
                    <p className="label-uppercase text-zinc-500">Return request</p>
                    <h1 className="text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50" style={{ letterSpacing: "-0.01em" }}>Pick items to return</h1>
                    <p className="mt-1 text-sm text-zinc-500">Order {order?.order_no || "…"}</p>

                    <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/jpg,image/webp" className="hidden" onChange={onFile} data-testid="return-photo-input" />

                    <ul className="mt-8 space-y-3" data-testid="return-items-list">
                        {(order?.items || []).map((it, idx) => {
                            const cur = items[idx] || { selected: false, qty: 1, reason: "damaged", notes: "", photos: [] };
                            const reason = REASONS.find((r) => r.value === cur.reason);
                            const photoRequired = reason?.photoReq;
                            return (
                                <li key={idx} className="rounded-2xl border border-zinc-100 bg-white p-5 dark:bg-white/[0.03]">
                                    <label className="flex cursor-pointer items-start gap-3">
                                        <input
                                            type="checkbox"
                                            checked={!!cur.selected}
                                            onChange={(e) => updateItem(idx, { selected: e.target.checked })}
                                            className="mt-1"
                                            data-testid={`return-item-${idx}-toggle`}
                                        />
                                        <div className="flex-1">
                                            <div className="flex items-start justify-between gap-3">
                                                <div>
                                                    <p className="font-medium text-zinc-900 dark:text-zinc-50">{it.name}</p>
                                                    <p className="mt-0.5 text-xs text-zinc-500">{it.brand} • {it.pack_size} • {inr(it.selling_price)} × {it.qty}</p>
                                                </div>
                                            </div>
                                            {cur.selected && (
                                                <div className="mt-4 space-y-3 border-t border-zinc-100 pt-4">
                                                    <div className="flex flex-wrap items-center gap-3">
                                                        <label className="text-xs text-zinc-500">Quantity</label>
                                                        <input
                                                            type="number" min={1} max={it.qty}
                                                            value={cur.qty}
                                                            onChange={(e) => updateItem(idx, { qty: Math.max(1, Math.min(it.qty, Number(e.target.value) || 1)) })}
                                                            className="w-20 rounded-md border border-zinc-200 px-2 py-1 text-sm"
                                                            data-testid={`return-item-${idx}-qty`}
                                                        />
                                                        <label className="text-xs text-zinc-500">Reason</label>
                                                        <select
                                                            value={cur.reason}
                                                            onChange={(e) => updateItem(idx, { reason: e.target.value })}
                                                            className="rounded-md border border-zinc-200 px-2 py-1 text-sm"
                                                            data-testid={`return-item-${idx}-reason`}
                                                        >
                                                            {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                                                        </select>
                                                    </div>
                                                    <textarea
                                                        rows={2}
                                                        value={cur.notes}
                                                        onChange={(e) => updateItem(idx, { notes: e.target.value })}
                                                        placeholder="Notes for the pharmacist (optional)…"
                                                        className="w-full rounded-md border border-zinc-200 p-2 text-sm"
                                                        data-testid={`return-item-${idx}-notes`}
                                                    />
                                                    <div>
                                                        <p className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
                                                            Photos {photoRequired && <span className="text-rose-600">*required</span>}
                                                        </p>
                                                        <div className="mt-2 flex flex-wrap gap-2">
                                                            {(cur.photos || []).map((p) => (
                                                                <div key={p.id} className="relative h-16 w-16 overflow-hidden rounded-md border border-zinc-200">
                                                                    <img src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} alt="" className="h-full w-full object-cover" />
                                                                    <button type="button" onClick={() => removePhoto(idx, p.id)} className="absolute right-0 top-0 rounded-bl-md bg-black/60 p-0.5 text-white">
                                                                        <X className="h-3 w-3" />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                            {(cur.photos || []).length < 4 && (
                                                                <button type="button" onClick={() => triggerUpload(idx)} className="flex h-16 w-16 items-center justify-center rounded-md border border-dashed border-zinc-300 text-zinc-500 hover:border-brand-300 hover:text-brand-700" data-testid={`return-item-${idx}-upload`}>
                                                                    <Camera className="h-5 w-5" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </label>
                                </li>
                            );
                        })}
                    </ul>

                    <div className="mt-6 flex items-center justify-between rounded-2xl border border-zinc-100 bg-zinc-50/60 p-5 dark:border-white/10 dark:bg-white/[0.03]">
                        <div>
                            <p className="text-xs text-zinc-500">Estimated refund</p>
                            <p className="text-xl font-semibold text-zinc-900 dark:text-zinc-50" data-testid="return-total-refund">{inr(totalRefund)}</p>
                        </div>
                        <button
                            disabled={submitting || selectedItems.length === 0}
                            onClick={submit}
                            className="btn-brand disabled:opacity-50"
                            data-testid="return-submit-btn"
                        >
                            {submitting ? "Submitting…" : "Request return"}
                        </button>
                    </div>
                    {error && (
                        <p className="mt-4 flex items-center gap-2 rounded-lg border border-red-100 bg-red-50 p-3 text-sm text-red-700" data-testid="return-error">
                            <AlertCircle className="h-4 w-4" /> {error}
                        </p>
                    )}
                </main>
                <Footer />
            </div>
        </AuthGuard>
    );
};

export default ReturnNew;
